// The parts the Administration tables share (Device, User and Role Management), so the three pages
// look and behave alike: the title row (title, filters, search box with its "Search by" field
// picker, extra icons), the filter drop-down, the "+" actions button, a row action icon with its
// tooltip, one date format, and browser-side search / sort / pages for lists the page already holds.
import React, { useEffect, useMemo, useState } from "react";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";
import Popover from "@mui/material/Popover";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import TuneIcon from "@mui/icons-material/Tune";

export const adminPageSizes = [25, 50, 100];

// one date and time format on every admin page, the table's "Last Message" format: 23/10/2026 02:24 am
export function adminDateTime(value) {
  const date = value ? new Date(value) : null;
  if (!date || isNaN(date)) return "-";
  return (
    date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " " +
    date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })
  );
}

// The title row: title, the filter drop-downs, the search box (with the "Search by" picker when
// `fields` is given: [{ label, value }]), extra icons, then the page icon.
export function AdminToolbar({ title, icon, filters, search, extra }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState([]);
  const openPicker = (e) => {
    setDraft(search.picked);
    setAnchorEl(e.currentTarget);
  };
  // the picker applies when it closes (the last ticked field cannot be unticked); the fields keep
  // the picker's order, so ticking them in another order is no change
  const closePicker = () => {
    setAnchorEl(null);
    const picked = search.fields.map((f) => f.value).filter((v) => draft.includes(v));
    if (picked.length && JSON.stringify(picked) !== JSON.stringify(search.picked)) search.onPick(picked);
  };
  return (
    <div style={{ margin: "15px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "5px" }}>
      <Typography component="p" sx={{ color: "rgb(191, 190, 200)", fontSize: "15px", fontWeight: "bold", flex: 1 }}>
        {title}
      </Typography>
      {filters}
      {search ? (
        <div style={{ marginRight: "20px", height: "40px", display: "flex", alignItems: "center" }}>
          <InputBase
            id="admin-search"
            placeholder={search.placeholder}
            sx={{
              border: "1px solid rgb(215, 215, 215)",
              borderRadius: "10px",
              padding: "12px",
              paddingRight: search.fields ? "40px" : "12px",
              width: "500px",
              maxWidth: "40vw",
              height: "40px",
            }}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            inputProps={{ "aria-label": "Search" }}
          />
          {search.fields ? (
            <>
              <TuneIcon id="admin-search-fields" sx={{ marginLeft: "-35px", cursor: "pointer", color: "#222" }} onClick={openPicker} />
              <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={closePicker} style={{ maxHeight: "500px" }}>
                <div style={{ padding: 20, paddingRight: 2, maxHeight: "500px" }}>
                  <span style={{ fontSize: "13px", color: "rgb(180, 180, 180)", marginBottom: 5 }}>Search by</span>
                  {search.fields.map((f) => (
                    <FormGroup key={f.value}>
                      <FormControlLabel
                        label={f.label}
                        control={
                          <Checkbox
                            checked={draft.includes(f.value)}
                            disabled={draft.length === 1 && draft.includes(f.value)}
                            onChange={(e) =>
                              setDraft((d) => (e.target.checked ? [...d, f.value] : d.filter((v) => v !== f.value)))
                            }
                          />
                        }
                      />
                    </FormGroup>
                  ))}
                </div>
              </Popover>
            </>
          ) : null}
        </div>
      ) : null}
      <div style={{ minWidth: "40px", display: "flex", justifyContent: "flex-end" }}>{extra}</div>
      {icon}
    </div>
  );
}

// A filter drop-down in the title row. options: [{ value, label }]. Single: value is one value and
// options carry their own "all" entry. Multiple: value is an array (empty = all), shown with a
// leading `allLabel` entry that clears the picks.
export function AdminSelect({ id, label, width, value, onChange, options, multiple, allLabel, renderValue }) {
  return (
    <div style={{ width, minWidth: 0 }}>
      <FormControl size="small" fullWidth>
        <InputLabel id={id} shrink>
          {label}
        </InputLabel>
        <Select
          multiple={multiple}
          displayEmpty
          notched
          labelId={id}
          label={label}
          value={value}
          onChange={(e) => onChange(multiple && e.target.value.includes("") ? [] : e.target.value)}
          renderValue={renderValue}
          MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
          sx={{ borderRadius: "10px" }}
        >
          {multiple ? (
            <MenuItem value="">
              <Checkbox size="small" checked={!value.length} />
              {allLabel}
            </MenuItem>
          ) : null}
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {multiple ? <Checkbox size="small" checked={value.includes(o.value)} /> : null}
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}

// The "+" button, fixed at the bottom right, opening to the left: actions [{ name, icon, onClick }]
export function AdminActions({ actions }) {
  const [open, setOpen] = useState(false);
  return (
    <SpeedDial
      ariaLabel="Actions"
      sx={{
        position: "fixed",
        bottom: "30px",
        right: "30px",
        transform: "translateX(30%)",
        zIndex: (theme) => theme.zIndex.speedDial,
      }}
      icon={<SpeedDialIcon />}
      FabProps={{ color: "secondary" }}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      direction="left"
    >
      {actions.map((a) => (
        <SpeedDialAction
          key={a.name}
          id={a.name}
          icon={a.icon}
          tooltipTitle={a.name}
          onClick={() => {
            setOpen(false);
            a.onClick();
          }}
        />
      ))}
    </SpeedDial>
  );
}

// One row action: the icon in the secondary colour, with its name in a tooltip
export function RowAction({ title, icon, onClick, disabled, id, ariaLabel }) {
  return (
    <Tooltip title={title} placement="bottom" arrow TransitionComponent={Zoom}>
      <span>
        <IconButton id={id} aria-label={ariaLabel || title} onClick={onClick} disabled={disabled} color="secondary">
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
const sortValue = (v) => (typeof v === "number" ? v : v == null ? "" : String(v));
const compare = (a, b) => (typeof a === "number" && typeof b === "number" ? a - b : collator.compare(String(a), String(b)));

// Search, sort and pages for a list the page already holds, for the shared table. search: the typed
// text; fields: { field: (row) => text } (the searched fields); picked: the fields ticked in "Search
// by"; sortable: { columnId: (row) => value }; defaultSort: { columnId: 1 | -1 } or none (the rows'
// own order); resetKey: anything that should send the table back to page 1 when it changes (filters).
// One column sorts at a time, as on Device Management.
export function useBrowserTable({ rows, search, fields, picked, sortable, defaultSort, resetKey }) {
  const blank = () => Object.fromEntries(Object.keys(sortable).map((k) => [k, 0]));
  const [sort, setSortState] = useState(() => ({ ...blank(), ...(defaultSort || {}) }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(adminPageSizes[0]);
  const setSort = (update) => {
    setPage(1);
    setSortState((prev) => {
      const next = typeof update == "function" ? update(prev) : update;
      const clicked = Object.keys(next).find((k) => next[k] !== prev[k]);
      if (!clicked) return prev;
      return { ...blank(), [clicked]: next[clicked] };
    });
  };
  const text = (search || "").trim().toLowerCase();
  useEffect(() => setPage(1), [text, JSON.stringify(picked), JSON.stringify(resetKey), pageSize]);
  const sortedBy = Object.keys(sort).find((k) => sort[k]);
  const ordered = useMemo(() => {
    const found = text
      ? rows.filter((row) => picked.some((f) => fields[f] && String(fields[f](row) ?? "").toLowerCase().includes(text)))
      : rows;
    if (!sortedBy) return found;
    const key = (r) => sortValue(sortable[sortedBy](r));
    return [...found].sort((a, b) => sort[sortedBy] * compare(key(a), key(b)));
  }, [rows, text, JSON.stringify(picked), sortedBy, sortedBy && sort[sortedBy]]);
  const totalPages = Math.ceil(ordered.length / pageSize);
  // a delete can empty the last page: step back to the new last page (as an update of the queued
  // page, so a search or filter that just sent the table back to page 1 keeps page 1)
  useEffect(() => {
    if (totalPages) setPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);
  const shown = Math.min(page, Math.max(totalPages, 1));
  return {
    rows: ordered.slice((shown - 1) * pageSize, shown * pageSize),
    page: shown,
    handleChange: setPage,
    totalPages,
    totalDocuments: ordered.length,
    pageSize,
    setPageSize,
    pageSizeOptions: adminPageSizes,
    sort,
    setSort,
  };
}
